/**
 * Precise visibility scoring module.
 * Uses mesh-backed raycasts against sampled surface points so the planner can prefer
 * candidates with genuinely visible target geometry when that richer context exists.
 */
import * as THREE from 'three'
import type { ViewpointVector3 } from '../generate-jig.types'
import { resolveVisibilityElements, type ViewpointVisibilityContext, type ViewpointVisibilityElement, type ViewpointVisibilitySample } from './visibility-context'

/**
 * Summary for a single occluder that blocked the sampled target rays most often.
 */
export interface BlockingOccluder {
  id: string
  blockedSamples: number
  blockingRatio: number
}

/**
 * Mesh-aware visibility score used when a richer scene representation is available.
 * Unlike the coarse scorer, this inspects sampled triangle centroids against real mesh
 * occluders so the planner can distinguish partial cover from fully clear views.
 */
export interface PreciseVisibilityMetrics {
  primaryVisibleRatio: number
  primaryVisibleSamples: number
  primaryTotalSamples: number
  contextVisibleRatio: number
  contextVisibleSamples: number
  contextTotalSamples: number
  blockingRatio: number
  topOccluders: BlockingOccluder[]
  quality: 'clear' | 'usable' | 'degraded'
}

const PRIMARY_SAMPLE_LIMIT = 12
const CONTEXT_SAMPLE_LIMIT = 6
const OCCLUDER_EPSILON = 1e-4
const PRECISE_CLEAR_PRIMARY_VISIBLE_RATIO = 0.75
const PRECISE_USABLE_PRIMARY_VISIBLE_RATIO = 0.5

function subtract(a: ViewpointVector3, b: ViewpointVector3): ViewpointVector3 {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
    z: a.z - b.z,
  }
}

function dot(a: ViewpointVector3, b: ViewpointVector3): number {
  return (a.x * b.x) + (a.y * b.y) + (a.z * b.z)
}

function pickFacingSamples(samples: ViewpointVisibilitySample[], cameraPosition: ViewpointVector3, limit: number): ViewpointVisibilitySample[] {
  const facingSamples = samples
    .map(function (sample: ViewpointVisibilitySample) {
      return {
        sample: sample,
        alignment: dot(sample.normal, subtract(cameraPosition, sample.point)),
      }
    })
    .filter(function (item) {
      return item.alignment > 0.05
    })
    .sort(function (a, b) {
      return b.alignment - a.alignment || b.sample.weight - a.sample.weight
    })
    .map(function (item) {
      return item.sample
    })

  if (facingSamples.length >= limit) {
    return facingSamples.slice(0, limit)
  }

  if (facingSamples.length > 0) {
    return facingSamples
  }

  return samples.slice(0, Math.min(limit, samples.length))
}

function buildOccluderSet(
  visibilityContext: ViewpointVisibilityContext,
  hiddenIds: Set<string>,
  ignoredIds: Set<string>
): ViewpointVisibilityElement[] {
  return visibilityContext.elements.filter(function (element: ViewpointVisibilityElement) {
    return !hiddenIds.has(element.id) && !ignoredIds.has(element.id)
  })
}

function scoreElements(
  cameraPosition: ViewpointVector3,
  targets: ViewpointVisibilityElement[],
  occluders: ViewpointVisibilityElement[],
  sampleLimit: number
): {
  visibleSamples: number
  totalSamples: number
  occluderCounts: Map<string, number>
} {
  const raycaster = new THREE.Raycaster()
  ;(raycaster as any).firstHitOnly = true
  const origin = new THREE.Vector3(cameraPosition.x, cameraPosition.y, cameraPosition.z)
  const direction = new THREE.Vector3()
  const targetPoint = new THREE.Vector3()
  const occluderCounts = new Map<string, number>()
  const occluderMeshes = occluders.map(function (element: ViewpointVisibilityElement) {
    return element.mesh
  })
  let visibleSamples = 0
  let totalSamples = 0

  targets.forEach(function (target: ViewpointVisibilityElement) {
    const samples = pickFacingSamples(target.samples, cameraPosition, sampleLimit)
    totalSamples += samples.length

    samples.forEach(function (sample: ViewpointVisibilitySample) {
      targetPoint.set(sample.point.x, sample.point.y, sample.point.z)
      direction.copy(targetPoint).sub(origin)
      const targetDistance = direction.length()

      if (targetDistance < 1e-8) {
        visibleSamples++
        return
      }

      direction.normalize()
      raycaster.set(origin, direction)
      raycaster.far = targetDistance - OCCLUDER_EPSILON

      const intersections = raycaster.intersectObjects(occluderMeshes, false)
      if (intersections.length === 0) {
        visibleSamples++
        return
      }

      const hitObjectName = intersections[0].object.userData && intersections[0].object.userData.objectName
      if (hitObjectName) {
        occluderCounts.set(hitObjectName, (occluderCounts.get(hitObjectName) || 0) + 1)
      }
    })
  })

  return {
    visibleSamples: visibleSamples,
    totalSamples: totalSamples,
    occluderCounts: occluderCounts,
  }
}

function toTopOccluders(occluderCounts: Map<string, number>, totalSamples: number): BlockingOccluder[] {
  return Array.from(occluderCounts.entries())
    .map(function (entry) {
      return {
        id: entry[0],
        blockedSamples: entry[1],
        blockingRatio: totalSamples > 0 ? entry[1] / totalSamples : 0,
      }
    })
    .sort(function (a, b) {
      return b.blockedSamples - a.blockedSamples
    })
    .slice(0, 3)
}

function classifyQuality(primaryVisibleRatio: number): PreciseVisibilityMetrics['quality'] {
  if (primaryVisibleRatio >= PRECISE_CLEAR_PRIMARY_VISIBLE_RATIO) {
    return 'clear'
  }

  if (primaryVisibleRatio >= PRECISE_USABLE_PRIMARY_VISIBLE_RATIO) {
    return 'usable'
  }

  return 'degraded'
}

/**
 * Scores a candidate by raycasting toward weighted surface samples on the target mesh.
 * Returns `null` when the requested target ids cannot be resolved in the visibility context.
 */
export function scorePreciseVisibility(params: {
  visibilityContext: ViewpointVisibilityContext
  cameraPosition: ViewpointVector3
  primaryTargetIds: string[]
  contextTargetIds: string[]
  occluderHiddenIds: Set<string>
}): PreciseVisibilityMetrics | null {
  const primaryTargets = params.primaryTargetIds.flatMap(function (id: string) {
    return resolveVisibilityElements(params.visibilityContext, id)
  })
  const contextTargets = params.contextTargetIds.flatMap(function (id: string) {
    return resolveVisibilityElements(params.visibilityContext, id)
  })

  if (primaryTargets.length === 0) {
    return null
  }

  const ignoredIds = new Set<string>([
    ...primaryTargets.map(function (target) { return target.id }),
    ...contextTargets.map(function (target) { return target.id }),
  ])
  const occluders = buildOccluderSet(params.visibilityContext, params.occluderHiddenIds, ignoredIds)

  const primary = scoreElements(params.cameraPosition, primaryTargets, occluders, PRIMARY_SAMPLE_LIMIT)
  const context = contextTargets.length > 0
    ? scoreElements(params.cameraPosition, contextTargets, occluders, CONTEXT_SAMPLE_LIMIT)
    : { visibleSamples: 0, totalSamples: 0, occluderCounts: new Map<string, number>() }

  const primaryVisibleRatio = primary.totalSamples > 0 ? primary.visibleSamples / primary.totalSamples : 0
  const contextVisibleRatio = context.totalSamples > 0 ? context.visibleSamples / context.totalSamples : 0
  const blockedPrimarySamples = primary.totalSamples - primary.visibleSamples
  const blockingRatio = primary.totalSamples > 0 ? blockedPrimarySamples / primary.totalSamples : 1
  const mergedOccluders = new Map(primary.occluderCounts)

  Array.from(context.occluderCounts.entries()).forEach(function (entry) {
    mergedOccluders.set(entry[0], (mergedOccluders.get(entry[0]) || 0) + entry[1])
  })

  return {
    primaryVisibleRatio: primaryVisibleRatio,
    primaryVisibleSamples: primary.visibleSamples,
    primaryTotalSamples: primary.totalSamples,
    contextVisibleRatio: contextVisibleRatio,
    contextVisibleSamples: context.visibleSamples,
    contextTotalSamples: context.totalSamples,
    blockingRatio: blockingRatio,
    topOccluders: toTopOccluders(mergedOccluders, Math.max(primary.totalSamples + context.totalSamples, 1)),
    quality: classifyQuality(primaryVisibleRatio),
  }
}

/**
 * Orders two precise visibility results from best to worst.
 * Lower return values indicate the candidate should win the comparison.
 */
export function comparePreciseVisibility(candidate: PreciseVisibilityMetrics | null, currentBest: PreciseVisibilityMetrics | null): number {
  if (candidate && !currentBest) {
    return -1
  }

  if (!candidate && currentBest) {
    return 1
  }

  if (!candidate && !currentBest) {
    return 0
  }

  if (candidate && currentBest && candidate.primaryVisibleRatio !== currentBest.primaryVisibleRatio) {
    return currentBest.primaryVisibleRatio - candidate.primaryVisibleRatio
  }

  if (candidate && currentBest && candidate.contextVisibleRatio !== currentBest.contextVisibleRatio) {
    return currentBest.contextVisibleRatio - candidate.contextVisibleRatio
  }

  if (candidate && currentBest && candidate.blockingRatio !== currentBest.blockingRatio) {
    return candidate.blockingRatio - currentBest.blockingRatio
  }

  return 0
}
